import { EventEmitter } from "eventemitter3";

// Read + JSON.parse a sessionStorage key without ever throwing. Guards against
// null/empty and the literal string "undefined" (which JSON.parse chokes on and
// would otherwise crash the whole app shell).
const readJSON = (key, store = sessionStorage) => {
  const raw = store.getItem(key);
  if (!raw || raw === "undefined") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Cache-invalidation bus for org-wide reference data.
 *
 * The setters below persist to sessionStorage AND emit a change event so that
 * any component subscribed via `useOrgData` (or `subscribeOrgData`) re-reads
 * the fresh value. After a create/update/delete, call the matching
 * `invalidateOrg*()` helper to clear the cache and notify subscribers, which
 * triggers a refetch. This fixes stale lists/dropdowns without a session reset.
 */
export const ORG_KEYS = {
  employees: "org-employees",
  departments: "org-departments",
  groups: "org-groups",
  shifts: "org-shifts",
  legalUnits: "sty-legal-unit",
  geofences: "geofence",
  activeLegalUnit: "sty-active-legal-unit",
  self: "org-self",
};

const orgEmitter = new EventEmitter();

export const subscribeOrgData = (key, callback) => {
  orgEmitter.on(key, callback);
  return () => orgEmitter.off(key, callback);
};

const emitOrgChange = (key) => orgEmitter.emit(key);

const invalidateOrgKey = (key) => {
  sessionStorage.removeItem(key);
  emitOrgChange(key);
};

export const invalidateOrgEmployees = () => invalidateOrgKey(ORG_KEYS.employees);
export const invalidateSelfEmployee = () => invalidateOrgKey(ORG_KEYS.self);
export const invalidateOrgDepartments = () => invalidateOrgKey(ORG_KEYS.departments);
export const invalidateOrgGroups = () => invalidateOrgKey(ORG_KEYS.groups);
export const invalidateOrgShifts = () => invalidateOrgKey(ORG_KEYS.shifts);
export const invalidateLegalUnits = () => invalidateOrgKey(ORG_KEYS.legalUnits);
export const invalidateGeofences = () => invalidateOrgKey(ORG_KEYS.geofences);

// localStorage, not sessionStorage: sessionStorage is per-tab, so opening the
// app in a second window found no organisation, gave up before it could use the
// refresh cookie — which is shared and still valid — and sent the person back to
// sign in. This is the org's name and id, not a credential; the credential stays
// in the httpOnly cookie.
export const setOrgDetails = (data) => {
  localStorage.setItem("sty-user-org", JSON.stringify(data));
};

export const getOrgDetails = () => readJSON("sty-user-org", localStorage);

// Every organisation this person belongs to. Kept beside the chosen one so the
// switcher can offer the others without another round trip — a contractor
// working for two clients should not have to sign out to move between them.
export const setOrgList = (list) => {
  localStorage.setItem("sty-user-orgs", JSON.stringify(list || []));
};
export const getOrgList = () => readJSON("sty-user-orgs", localStorage) || [];

// The Organization model stores `companyName`; nothing sets `name`. Reading
// `org.name` gave "Organisation" everywhere an org was shown, and made the
// switcher render nothing at all because it tests for a name before drawing.
export const orgName = (org) =>
  org?.companyName || org?.name || "Organisation";

/**
 * Move to another organisation.
 *
 * Every ORG_KEYS cache is scoped to one organisation and none of them carry the
 * org id, so switching without clearing them shows the previous client's
 * employees, departments and shifts under the new client's name. That is the
 * one thing this must not do.
 */
export const switchOrg = (org) => {
  Object.values(ORG_KEYS).forEach((key) => sessionStorage.removeItem(key));
  setOrgDetails(org);
  window.location.pathname = "/app";
};

// Everything a session leaves behind. sessionStorage.clear() alone no longer
// covers it now that the chosen organisation outlives the tab.
export const clearSession = () => {
  sessionStorage.clear();
  localStorage.removeItem("sty-user-org");
  localStorage.removeItem("sty-user-orgs");
};

/**
 * "I'll finish my profile later."
 *
 * Held for the session rather than the account: the point is not to let
 * somebody dismiss onboarding for ever, it is that a new joiner who does not
 * have their PAN to hand can still mark attendance today. Next time they sign
 * in they are asked again, and the checklist and the weekly nudge keep asking
 * in between.
 */
export const deferProfile = () => sessionStorage.setItem("sty-profile-deferred", "1");
export const profileDeferred = () => sessionStorage.getItem("sty-profile-deferred") === "1";

let onLoginEmployeeInfo;
export const setOnLoginEmployeeInfo = (data) => (onLoginEmployeeInfo = data);
export const getOnLoginEmployeeInfo = () => onLoginEmployeeInfo;

export const getLegalUnitList = () => readJSON("sty-legal-unit");
export const setLegalUnitList = (list) => {
  const stringifyList = JSON.stringify(list);
  sessionStorage.setItem("sty-legal-unit", stringifyList);
  emitOrgChange(ORG_KEYS.legalUnits);
};

// Active legal entity — the entity the admin is currently working in. Persisted
// (survives reload) and emitted on the bus so subscribers can react to a switch.
// Stores the full entity object (geofence.js relies on that).
export const getActiveLegalUnit = () => readJSON(ORG_KEYS.activeLegalUnit);
export const setActiveLegalUnit = (entity) => {
  sessionStorage.setItem(ORG_KEYS.activeLegalUnit, JSON.stringify(entity));
  // Entity-scoped data cached under org-wide keys. Switching entity left the
  // previous entity's copy in place — the map showed the wrong geofence, and
  // once departments became entity-scoped the pickers would have kept the
  // previous entity's list for the rest of the session, because sessionStorage
  // survives the reload the switcher does. Cleared after the new entity is
  // stored, so the refetch each triggers resolves against the new one.
  invalidateOrgKey(ORG_KEYS.geofences);
  invalidateOrgKey(ORG_KEYS.departments);
  emitOrgChange(ORG_KEYS.activeLegalUnit);
};
export const getActiveLegalUnitId = () => getActiveLegalUnit()?._id;

// Make sure there is a valid active entity: default to the first, and re-select
// the first if the stored one is gone (e.g. it was deleted). Returns the entity.
export const ensureActiveLegalUnit = () => {
  const list = getLegalUnitList() || [];
  if (list.length === 0) return null;
  const current = getActiveLegalUnit();
  const stillValid = current && list.some((u) => u._id === current._id);
  if (!stillValid) {
    setActiveLegalUnit(list[0]);
    return list[0];
  }
  return current;
};

let geofencesList;
// export const getGeofenceList = () => geofencesList;
// export const setGeofenceList = (list) => (geofencesList = list);

export const getGeofenceList = () => readJSON("geofence");
export const setGeofenceList = (list) => {
  const stringifyList = JSON.stringify(list);
  sessionStorage.setItem("geofence", stringifyList);
  emitOrgChange(ORG_KEYS.geofences);
};

export const setOrgEmployees = (data) => {
  const employees = JSON.stringify(data);
  sessionStorage.setItem("org-employees", employees);
  emitOrgChange(ORG_KEYS.employees);
};

export const getOrgEmployees = () => readJSON("org-employees");

export const setOrgDepartments = (data) => {
  const departments = JSON.stringify(data);
  sessionStorage.setItem("org-departments", departments);
  emitOrgChange(ORG_KEYS.departments);
};

export const getOrgDepartments = () => readJSON("org-departments");

export const setOrgGroups = (data) => {
  const groups = JSON.stringify(data);
  sessionStorage.setItem("org-groups", groups);
  emitOrgChange(ORG_KEYS.groups);
};

export const getOrgGroups = () => readJSON("org-groups");

export const setOrgShifts = (data) => {
  const shifts = JSON.stringify(data);
  sessionStorage.setItem("org-shifts", shifts);
  emitOrgChange(ORG_KEYS.shifts);
};

export const getOrgShifts = () => readJSON("org-shifts");